import { useEffect, useState, useCallback } from "react";
import api, { mediaUrl } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { RichText } from "@/components/RichText";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { GraduationCap, BookOpen, Plus, Pencil, Trash2, Paperclip, Upload, FileText, Download, Check, CircleCheck, Circle } from "lucide-react";
import { toast } from "sonner";

const KIND_META = {
  estudo: { label: "Estudos do Mês", icon: BookOpen, subtitleLabel: "Mês / Período", subtitlePh: "Ex: Junho 2026" },
  escola: { label: "Escola de Liderança", icon: GraduationCap, subtitleLabel: "Módulo / Tópico", subtitlePh: "Ex: Módulo 1 · Fundamentos" },
};

function ItemForm({ kind, initial, nextOrder, onSaved }) {
  const [open, setOpen] = useState(false);
  const meta = KIND_META[kind];
  const isEdit = !!initial;
  const [form, setForm] = useState(
    initial || { kind, title: "", subtitle: "", body: "", order: nextOrder }
  );
  useEffect(() => { if (open) setForm(initial || { kind, title: "", subtitle: "", body: "", order: nextOrder }); }, [open, initial, kind, nextOrder]);

  const save = async () => {
    if (!form.title.trim()) return toast.error("Informe o título");
    const payload = { kind, title: form.title, subtitle: form.subtitle, body: form.body, order: Number(form.order) || 0 };
    try {
      if (isEdit) await api.put(`/learn/${initial.id}`, payload);
      else await api.post("/learn", payload);
      toast.success(isEdit ? "Atualizado!" : "Criado!");
      setOpen(false);
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao salvar"); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {isEdit
          ? <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`edit-learn-${initial.id}`}><Pencil className="h-4 w-4" /></Button>
          : <Button className="gap-2" data-testid={`add-learn-${kind}`}><Plus className="h-4 w-4" /> Novo</Button>}
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader><DialogTitle>{isEdit ? "Editar" : "Novo"} · {meta.label}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Título</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="learn-title-input" className="mt-1.5" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>{meta.subtitleLabel}</Label><Input value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} placeholder={meta.subtitlePh} data-testid="learn-subtitle-input" className="mt-1.5" /></div>
            <div><Label>Ordem</Label><Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className="mt-1.5" /></div>
          </div>
          <div>
            <Label>Conteúdo (Markdown)</Label>
            <p className="text-xs text-muted-foreground mb-1.5">Use <code>**negrito**</code>, <code># Título</code>, listas com <code>-</code>.</p>
            <Textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={10} data-testid="learn-body-input" />
          </div>
        </div>
        <DialogFooter><Button onClick={save} className="gap-2" data-testid="save-learn-button"><Check className="h-4 w-4" /> Salvar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Attachments({ item, isAdmin, onChanged }) {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/learn/${item.id}/attachment`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Anexo enviado!");
      onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro no upload"); }
    setBusy(false);
    e.target.value = "";
  };
  const remove = async (path) => {
    try { await api.delete(`/learn/${item.id}/attachment`, { data: { path } }); toast.success("Anexo removido"); onChanged(); }
    catch { toast.error("Erro"); }
  };
  const atts = item.attachments || [];
  if (atts.length === 0 && !isAdmin) return null;
  return (
    <div className="mt-4 border-t border-border pt-3">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
        <Paperclip className="h-3.5 w-3.5" /> Materiais de apoio
      </div>
      <div className="space-y-1.5">
        {atts.map((a) => (
          <div key={a.path} className="flex items-center justify-between gap-2 rounded-lg border border-border px-3 py-2" data-testid="attachment-row">
            <a href={mediaUrl(a.path)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-primary hover:underline min-w-0" data-testid={`attachment-link`}>
              <FileText className="h-4 w-4 shrink-0" /> <span className="truncate">{a.name}</span> <Download className="h-3.5 w-3.5 shrink-0" />
            </a>
            {isAdmin && <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => remove(a.path)} data-testid="remove-attachment-button"><Trash2 className="h-4 w-4 text-destructive" /></Button>}
          </div>
        ))}
        {atts.length === 0 && <p className="text-xs text-muted-foreground">Nenhum material anexado.</p>}
      </div>
      {isAdmin && (
        <label className="mt-2 inline-flex items-center gap-1.5 text-sm text-primary cursor-pointer hover:underline" data-testid={`upload-attachment-${item.id}`}>
          <Upload className="h-4 w-4" /> {busy ? "Enviando…" : "Anexar arquivo (PDF, DOC, imagem — máx 25MB)"}
          <input type="file" className="hidden" onChange={upload} disabled={busy} accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,image/*" />
        </label>
      )}
    </div>
  );
}

function KindTab({ kind, isAdmin }) {
  const [items, setItems] = useState([]);
  const meta = KIND_META[kind];
  const load = useCallback(() => {
    api.get("/learn", { params: { kind } }).then((r) => setItems(r.data)).catch(() => {});
  }, [kind]);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    try { await api.delete(`/learn/${id}`); toast.success("Removido"); load(); }
    catch { toast.error("Erro ao remover"); }
  };
  const toggleComplete = async (item) => {
    const next = !item.completed;
    setItems((arr) => arr.map((i) => (i.id === item.id ? { ...i, completed: next } : i)));
    try { await api.post(`/learn/${item.id}/complete`, { completed: next }); toast.success(next ? "Marcado como concluído!" : "Marcação removida"); }
    catch { toast.error("Erro"); load(); }
  };
  const nextOrder = (items.reduce((m, i) => Math.max(m, i.order || 0), 0) || 0) + 1;
  const doneCount = items.filter((i) => i.completed).length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        {items.length > 0 && (
          <span className="text-sm text-muted-foreground" data-testid={`progress-${kind}`}>
            <strong className="text-foreground">{doneCount}</strong> de {items.length} concluído{items.length > 1 ? "s" : ""}
          </span>
        )}
        {isAdmin && <ItemForm kind={kind} nextOrder={nextOrder} onSaved={load} />}
      </div>
      {items.length === 0 ? (
        <Card className="p-12 text-center text-muted-foreground">
          <meta.icon className="h-10 w-10 mx-auto mb-3 opacity-50" /> Nenhum conteúdo publicado ainda.
        </Card>
      ) : (
        <div className="space-y-4">
          {items.map((item) => (
            <Card key={item.id} className={`p-6 ${item.completed ? "border-[hsl(var(--emerald))]/40" : ""}`} data-testid="learn-item-card">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-2 min-w-0">
                  {item.completed && <CircleCheck className="h-5 w-5 text-[hsl(var(--emerald))] mt-1 shrink-0" />}
                  <div>
                    <h3 className="font-display text-xl font-semibold">{item.title}</h3>
                    {item.subtitle && <p className="text-sm text-primary mt-0.5">{item.subtitle}</p>}
                  </div>
                </div>
                {isAdmin && (
                  <div className="flex items-center gap-1 shrink-0">
                    <ItemForm kind={kind} initial={item} nextOrder={nextOrder} onSaved={load} />
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`delete-learn-${item.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Remover "{item.title}"?</AlertDialogTitle>
                          <AlertDialogDescription>O conteúdo e seus anexos serão removidos.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction onClick={() => del(item.id)}>Remover</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                )}
              </div>
              {item.body && <div className="mt-3"><RichText>{item.body}</RichText></div>}
              <Attachments item={item} isAdmin={isAdmin} onChanged={load} />
              <div className="mt-4 border-t border-border pt-3">
                <Button
                  variant={item.completed ? "outline" : "default"}
                  size="sm"
                  className="gap-1.5"
                  onClick={() => toggleComplete(item)}
                  data-testid={`toggle-complete-${item.id}`}
                >
                  {item.completed ? <><Circle className="h-4 w-4" /> Marcar como não concluído</> : <><CircleCheck className="h-4 w-4" /> Marcar como concluído</>}
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Formacao() {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin || user?.role === "coordenador";

  return (
    <div className="space-y-8 animate-fade-up" data-testid="formacao-page">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight flex items-center gap-2">
          <GraduationCap className="h-8 w-8 text-primary" /> Estudos & Formação
        </h1>
        <p className="text-muted-foreground mt-1">Estudos do mês e a Escola de Liderança dos PGDs.</p>
      </div>

      <Tabs defaultValue="estudo">
        <TabsList>
          <TabsTrigger value="estudo" data-testid="tab-estudos">Estudos do Mês</TabsTrigger>
          <TabsTrigger value="escola" data-testid="tab-escola">Escola de Liderança</TabsTrigger>
        </TabsList>
        <TabsContent value="estudo" className="mt-6"><KindTab kind="estudo" isAdmin={isAdmin} /></TabsContent>
        <TabsContent value="escola" className="mt-6"><KindTab kind="escola" isAdmin={isAdmin} /></TabsContent>
      </Tabs>
    </div>
  );
}
